// src/components/employee/EmployeeHeader.js
import React, { useState, useEffect, useRef } from 'react';
import { Bell, Search, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import UserDropdown from '../common/UserDropdown';
import BugReportButton from '../common/BugReportButton';
import { useTheme } from '../../contexts/ThemeContext';
import { useEmployees } from '../../contexts/EmployeeContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { useAuth } from '../../hooks/useAuth';

const EmployeeHeader = ({ user, onLogout }) => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { employees } = useEmployees();
    const { notifications, unreadCount, markAsRead, getNotificationsForUser, getUnreadCount } = useNotifications();
    const { company } = useAuth();
    const [showNotifications, setShowNotifications] = useState(false);
    const [showAllNotifications, setShowAllNotifications] = useState(false);
    const [notificationFilter, setNotificationFilter] = useState('all'); // 'all', 'personal', 'team'
    const accessMode = location.pathname.startsWith('/admin') ? 'admin' : 'my';
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchResults, setShowSearchResults] = useState(false);
    const notificationRef = useRef(null);

    // Helper function to categorize notification types
    const categorizeNotification = (notification) => {
        const type = notification.type;
        if (!type) return 'personal';

        // Personal notifications - user-specific
        const personalTypes = [
            'personal',
            'onboarding_complete',           // Onboarding completed (self)
            'kudos_mention',                 // Kudos mentions
            'vibe_mention',                  // Vibe post/comment mentions
            'vibe_reaction',                 // Reaction on user's vibe post/comment
            'vibe_comment',                  // Comment on user's vibe post
            'task_completed',                // Task completed (self)
            'task_overdue',                  // Task overdue (self)
            'task_assigned',                 // Task assigned to user
            'birthday',                      // User's birthday
            'anniversary',                   // User's work anniversary
            'payroll_processed',             // Salary slip generated (self)
            'leave_approved',                // Leave approved (self)
            'leave_rejected',                // Leave rejected (self)
            'attendance_alert',              // Attendance alert (self)
            'document_uploaded',             // Document uploaded (self)
            'profile_update'                 // Profile updated (self)
        ];

        // Team notifications - team management
        const teamTypes = [
            'team',
            'team_onboarding',               // New team member onboarded
            'team_task_assigned',            // Team task assigned by manager/admin
            'team_task_completed',           // Team task completed
            'team_task_overdue',             // Team task overdue
            'team_leave_request',            // Reportee leave request
            'team_attendance_request',       // Reportee attendance approval request
            'team_flexi_request',            // Reportee flexi component request
            'team_wfh_request',              // Reportee WFH request
            'team_performance',              // Team performance notifications
            'approval_request'               // General approval requests from reportees
        ];

        if (personalTypes.includes(type)) return 'personal';
        if (teamTypes.includes(type)) return 'team';

        return 'personal'; // Default to personal
    };

    // Filter notifications based on selected filter
    const filteredNotifications = notifications.filter(notification => {
        if (notificationFilter === 'all') return true;
        return categorizeNotification(notification) === notificationFilter;
    });

    // Click outside to close notifications
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (notificationRef.current && !notificationRef.current.contains(event.target)) {
                setShowNotifications(false);
            }
        };

        if (showNotifications) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showNotifications]);

    const handleAccessToggle = () => {
        if (accessMode === 'my') {
            // Switch to Admin access
            navigate('/admin/dashboard');
        } else {
            // Switch back to My access
            const secondaryAccess = user?.secondaryAccess || user?.secondary_access || 'employee';
            if (secondaryAccess === 'manager') {
                navigate('/manager');
            } else {
                navigate('/employee');
            }
        }
    };

    // Check if user has admin access
    const hasAdminAccess = user?.accessLevel === 'admin' || user?.access_level === 'admin' || user?.role === 'admin' || user?.is_admin;

    // Navigation sections for quick access (Employee portal sections)
    const navigationSections = [
        { name: 'Dashboard', path: '/employee/dashboard', category: 'Main' },
        { name: 'My Profile', path: '/employee/profile', category: 'Profile' },
        { name: 'Profile', path: '/employee/profile', category: 'Profile' },
        { name: 'Onboarding', path: '/employee/onboarding', category: 'Onboarding' },
        { name: 'My Onboarding', path: '/employee/onboarding', category: 'Onboarding' },
        { name: 'Attendance', path: '/employee/attendance', category: 'Attendance' },
        { name: 'My Attendance', path: '/employee/attendance', category: 'Attendance' },
        { name: 'Leaves', path: '/employee/leaves', category: 'Leaves' },
        { name: 'My Leaves', path: '/employee/leaves', category: 'Leaves' },
        { name: 'Leave Requests', path: '/employee/leaves', category: 'Leaves' },
        { name: 'Payroll', path: '/employee/payroll', category: 'Payroll' },
        { name: 'My Payroll', path: '/employee/payroll', category: 'Payroll' },
        { name: 'Payslips', path: '/employee/payroll', category: 'Payroll' },
        { name: 'Salary', path: '/employee/payroll', category: 'Payroll' },
        { name: 'Documents', path: '/employee/documents', category: 'Documents' },
        { name: 'My Documents', path: '/employee/documents', category: 'Documents' },
        { name: 'Team', path: '/employee/team', category: 'Team' },
        { name: 'My Team', path: '/employee/team', category: 'Team' },
        // Admin sections if user has admin access
        ...(hasAdminAccess ? [
            { name: 'Admin Dashboard', path: '/admin/dashboard', category: 'Admin' },
            { name: 'Employee Management', path: '/admin/employees', category: 'Admin' },
            { name: 'Functional Structure', path: '/admin/org-view/functional', category: 'Admin' },
            { name: 'Payroll Run', path: '/admin/payrollrun', category: 'Admin' },
        ] : [])
    ];

    // Filter employees based on search query
    const filteredEmployees = searchQuery.trim()
        ? employees.filter(emp => {
            const query = searchQuery.toLowerCase();
            const fullName = `${emp.first_name} ${emp.last_name}`.toLowerCase();
            const displayName = emp.display_name?.toLowerCase() || '';
            const email = emp.email?.toLowerCase() || '';
            const empCode = emp.employee_code?.toLowerCase() || '';

            return fullName.includes(query) ||
                   displayName.includes(query) ||
                   email.includes(query) ||
                   empCode.includes(query);
        }).slice(0, 3) // Limit to 3 results
        : [];

    // Filter navigation sections based on search query
    const filteredSections = searchQuery.trim()
        ? navigationSections.filter(section => {
            const query = searchQuery.toLowerCase();
            return section.name.toLowerCase().includes(query) ||
                   section.category.toLowerCase().includes(query);
        }).slice(0, 5) // Limit to 5 results
        : [];

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);
        setShowSearchResults(value.trim().length > 0);
    };

    const handleEmployeeSelect = (employeeId) => {
        // Navigate to employee profile view (not admin view)
        navigate(`/employee/view-profile/${employeeId}`);
        setSearchQuery('');
        setShowSearchResults(false);
    };

    const handleSectionSelect = (path) => {
        navigate(path);
        setSearchQuery('');
        setShowSearchResults(false);
    };

    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            if (filteredSections.length > 0) {
                handleSectionSelect(filteredSections[0].path);
            } else if (filteredEmployees.length > 0) {
                handleEmployeeSelect(filteredEmployees[0].id);
            }
        }
    };

    return (
        <header className="bg-white dark:bg-gray-800 shadow-sm px-6 py-4 border-b dark:border-gray-700 transition-colors duration-200">
            <div className="flex items-center justify-between">
                {/* Company Name and Logo */}
                <div className="flex items-center gap-3 mr-6">
                    {company?.logo && (
                        <img
                            src={company.logo}
                            alt={company.name || 'Company Logo'}
                            className="h-10 w-10 object-contain"
                        />
                    )}
                    <span className="text-lg font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                        {company?.name || 'Company'}
                    </span>
                </div>

                <div className="flex items-center flex-1">
                    <div className="relative w-96">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5" />
                        <input
                            type="text"
                            placeholder="Search employees..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onKeyPress={handleSearchKeyPress}
                            onFocus={() => searchQuery.trim() && setShowSearchResults(true)}
                            className="w-full pl-10 pr-4 py-2 border dark:border-gray-600 rounded-lg
                                     bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                                     placeholder-gray-500 dark:placeholder-gray-400
                                     focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                        />

                        {/* Search Results Dropdown */}
                        {showSearchResults && (filteredSections.length > 0 || filteredEmployees.length > 0) && (
                            <>
                                <div
                                    className="fixed inset-0 z-10"
                                    onClick={() => setShowSearchResults(false)}
                                />
                                <div className={`absolute top-full left-0 right-0 mt-2 rounded-lg shadow-xl border z-20 max-h-80 overflow-y-auto ${
                                    theme === 'dark'
                                        ? 'bg-gray-800 border-gray-700'
                                        : 'bg-white border-gray-200'
                                }`}>
                                    {/* Navigation Sections */}
                                    {filteredSections.length > 0 && (
                                        <div>
                                            <div className={`px-4 py-2 text-xs font-semibold uppercase ${
                                                theme === 'dark' ? 'text-gray-400 bg-gray-900' : 'text-gray-500 bg-gray-50'
                                            }`}>
                                                Navigation
                                            </div>
                                            {filteredSections.map((section, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => handleSectionSelect(section.path)}
                                                    className={`w-full text-left px-4 py-3 hover:bg-opacity-50 border-b transition-colors ${
                                                        theme === 'dark'
                                                            ? 'border-gray-700 hover:bg-gray-700'
                                                            : 'border-gray-200 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                                            theme === 'dark' ? 'bg-blue-900' : 'bg-blue-100'
                                                        }`}>
                                                            <Search className={`w-5 h-5 ${
                                                                theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                                                            }`} />
                                                        </div>
                                                        <div className="flex-1">
                                                            <p className={`text-sm font-medium ${
                                                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                            }`}>
                                                                {section.name}
                                                            </p>
                                                            <p className={`text-xs ${
                                                                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                                            }`}>
                                                                {section.category}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {/* Employees */}
                                    {filteredEmployees.length > 0 && (
                                        <div>
                                            <div className={`px-4 py-2 text-xs font-semibold uppercase ${
                                                theme === 'dark' ? 'text-gray-400 bg-gray-900' : 'text-gray-500 bg-gray-50'
                                            }`}>
                                                Employees
                                            </div>
                                            {filteredEmployees.map((employee) => (
                                                <button
                                                    key={employee.id}
                                                    onClick={() => handleEmployeeSelect(employee.id)}
                                                    className={`w-full text-left px-4 py-3 hover:bg-opacity-50 border-b transition-colors ${
                                                        theme === 'dark'
                                                            ? 'border-gray-700 hover:bg-gray-700'
                                                            : 'border-gray-200 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                                                            {employee.first_name?.[0]}{employee.last_name?.[0]}
                                                        </div>
                                                        <div className="flex-1">
                                                            <p className={`text-sm font-medium ${
                                                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                            }`}>
                                                                {employee.first_name} {employee.last_name}
                                                            </p>
                                                            <p className={`text-xs ${
                                                                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                                            }`}>
                                                                {employee.designation} • {employee.employee_code}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    {/* Access Toggle Switch - Only for admin users */}
                    {hasAdminAccess && (
                        <div className="flex items-center gap-3 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                            <span className={`text-sm font-medium ${
                                accessMode === 'admin'
                                    ? 'text-purple-600 dark:text-purple-400'
                                    : 'text-gray-500 dark:text-gray-400'
                            }`}>
                                Admin
                            </span>
                            <button
                                onClick={handleAccessToggle}
                                className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors ${
                                    accessMode === 'my'
                                        ? 'bg-green-600'
                                        : 'bg-purple-600'
                                }`}
                            >
                                <span
                                    className={`inline-block w-4 h-4 transform transition-transform bg-white rounded-full ${
                                        accessMode === 'my' ? 'translate-x-6' : 'translate-x-1'
                                    }`}
                                />
                            </button>
                            <span className={`text-sm font-medium ${
                                accessMode === 'my'
                                    ? 'text-green-600 dark:text-green-400'
                                    : 'text-gray-500 dark:text-gray-400'
                            }`}>
                                My Access
                            </span>
                        </div>
                    )}

                    {/* Bug report */}
                    <BugReportButton />

                    {/* Notifications */}
                    <div className="relative" ref={notificationRef}>
                        <button
                            onClick={() => setShowNotifications(!showNotifications)}
                            className="relative p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                            <Bell className="w-5 h-5" />
                            {unreadCount > 0 && (
                                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                    {unreadCount}
                                </span>
                            )}
                        </button>

                        {/* Notifications dropdown */}
                        {showNotifications && (
                            <div className={`absolute right-0 mt-2 w-80 rounded-lg shadow-lg border z-50 ${
                                theme === 'dark'
                                    ? 'bg-gray-800 border-gray-700'
                                    : 'bg-white border-gray-200'
                            }`}>
                                <div className={`p-4 border-b ${
                                    theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                                }`}>
                                    <h3 className={`font-semibold mb-3 ${
                                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                                    }`}>
                                        Notifications
                                    </h3>
                                    {/* Filter Toggle */}
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setNotificationFilter('all')}
                                            className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                                notificationFilter === 'all'
                                                    ? theme === 'dark'
                                                        ? 'bg-blue-600 text-white'
                                                        : 'bg-blue-500 text-white'
                                                    : theme === 'dark'
                                                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                        >
                                            All
                                        </button>
                                        <button
                                            onClick={() => setNotificationFilter('personal')}
                                            className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                                notificationFilter === 'personal'
                                                    ? theme === 'dark'
                                                        ? 'bg-blue-600 text-white'
                                                        : 'bg-blue-500 text-white'
                                                    : theme === 'dark'
                                                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                        >
                                            Personal
                                        </button>
                                        <button
                                            onClick={() => setNotificationFilter('team')}
                                            className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                                notificationFilter === 'team'
                                                    ? theme === 'dark'
                                                        ? 'bg-blue-600 text-white'
                                                        : 'bg-blue-500 text-white'
                                                    : theme === 'dark'
                                                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                        >
                                            Team
                                        </button>
                                    </div>
                                </div>
                                <div className="max-h-64 overflow-y-auto">
                                    {filteredNotifications.length === 0 ? (
                                        <div className="p-8 text-center">
                                            <p className={`text-sm ${
                                                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                            }`}>
                                                No {notificationFilter !== 'all' ? notificationFilter : ''} notifications
                                            </p>
                                        </div>
                                    ) : (
                                        filteredNotifications.map(notification => (
                                            <div
                                                key={notification.id}
                                                className={`p-4 border-b hover:bg-opacity-50 cursor-pointer ${
                                                    theme === 'dark'
                                                        ? 'border-gray-700 hover:bg-gray-700'
                                                        : 'border-gray-200 hover:bg-gray-50'
                                                }`}
                                                onClick={() => markAsRead(notification.id)}
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1">
                                                        <h4 className={`font-medium text-sm ${
                                                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                        }`}>
                                                            {notification.title}
                                                        </h4>
                                                        <p className={`text-sm mt-1 ${
                                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                                                        }`}>
                                                            {notification.message}
                                                        </p>
                                                        <p className={`text-xs mt-1 ${
                                                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                                        }`}>
                                                            {new Date(notification.timestamp).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                                                        </p>
                                                    </div>
                                                    {!notification.read && (
                                                        <div className="w-2 h-2 bg-blue-500 rounded-full ml-2 mt-1"></div>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                                <div className={`p-3 text-center border-t ${
                                    theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                                }`}>
                                    <button
                                        onClick={() => {
                                            setShowAllNotifications(true);
                                            setShowNotifications(false);
                                        }}
                                        className={`text-sm font-medium ${
                                            theme === 'dark' ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'
                                        }`}
                                    >
                                        View All Notifications
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* All Notifications Modal */}
                    {showAllNotifications && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                            <div className={`w-full max-w-2xl max-h-[80vh] rounded-lg shadow-xl flex flex-col ${
                                theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                            }`}>
                                {/* Modal Header */}
                                <div className={`p-4 border-b flex items-center justify-between ${
                                    theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                                }`}>
                                    <h2 className={`text-lg font-semibold ${
                                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                                    }`}>
                                        All Notifications
                                    </h2>
                                    <button
                                        onClick={() => setShowAllNotifications(false)}
                                        className={`p-1 rounded-lg ${
                                            theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                                        }`}
                                    >
                                        <X className={`w-5 h-5 ${
                                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                        }`} />
                                    </button>
                                </div>

                                {/* Modal Content - Scrollable */}
                                <div className="flex-1 overflow-y-auto p-4">
                                    {filteredNotifications.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center h-full py-12">
                                            <Bell className={`w-16 h-16 mb-4 ${
                                                theme === 'dark' ? 'text-gray-600' : 'text-gray-300'
                                            }`} />
                                            <p className={`text-sm ${
                                                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                            }`}>
                                                No {notificationFilter !== 'all' ? notificationFilter : ''} notifications yet
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {filteredNotifications.map(notification => (
                                                <div
                                                    key={notification.id}
                                                    className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                                                        !notification.read
                                                            ? theme === 'dark'
                                                                ? 'bg-blue-900 bg-opacity-20 border-blue-700'
                                                                : 'bg-blue-50 border-blue-200'
                                                            : theme === 'dark'
                                                            ? 'bg-gray-700 border-gray-600 hover:bg-gray-650'
                                                            : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                                                    }`}
                                                    onClick={() => markAsRead(notification.id)}
                                                >
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <h4 className={`font-medium text-sm ${
                                                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                                }`}>
                                                                    {notification.title}
                                                                </h4>
                                                                {!notification.read && (
                                                                    <span className="px-2 py-0.5 text-xs font-medium bg-blue-500 text-white rounded">
                                                                        New
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className={`text-sm mt-1 ${
                                                                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                                                            }`}>
                                                                {notification.message}
                                                            </p>
                                                            <p className={`text-xs mt-2 ${
                                                                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                                            }`}>
                                                                {new Date(notification.timestamp).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Modal Footer */}
                                <div className={`p-4 border-t ${
                                    theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                                }`}>
                                    <button
                                        onClick={() => setShowAllNotifications(false)}
                                        className={`w-full px-4 py-2 rounded-lg font-medium ${
                                            theme === 'dark'
                                                ? 'bg-gray-700 text-white hover:bg-gray-600'
                                                : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
                                        }`}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex items-center gap-3 border-l dark:border-gray-700 pl-4">
                        <div className="text-right">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {user?.first_name} {user?.last_name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{user?.designation || 'Employee'}</p>
                        </div>

                        <UserDropdown user={user} onLogout={onLogout} isAdmin={false} />
                    </div>
                </div>
            </div>
        </header>
    );
};
export default EmployeeHeader;